// A puff of grilled mochi: a pressure soft body in the manner of Matyka &
// Ollila ("Pressure Model of Soft Body Simulation", SIGRAD 2003). The skin is
// a round net of springs whose rim stays on the mochi. Gas under it follows
// the ideal-gas law, p = nT / V, where V is a reservoir inside the mochi plus
// the volume under the skin. Heat raises T and softens the skin, so the puff
// swells slowly at first and then quickly; popping lets the gas out and the
// slack skin settles into wrinkles.
export class PressurePuff{
  // center/normal: the weak spot on the mochi; radius: the skin's rim radius.
  // lean: a sideways pull (a share of the skin's weight), so a big puff tips.
  constructor({center,normal=[0,1,0],radius,rings=9,segments=24,lean=[0,0,0],random=Math.random}){
    this.center=[...center];this.normal=[...normal];this.radius=radius;this.rings=rings;this.segments=segments;this.lean=[...lean];
    const n=1+rings*segments;this.count=n;
    this.x=new Float64Array(n*3);this.prev=new Float64Array(n*3);this.base=new Float64Array(n*3);this.fixed=new Uint8Array(n);
    // Tangent frame on the surface.
    const [nx,ny,nz]=this.normal,t=Math.abs(nx)<.9?[1,0,0]:[0,1,0];
    let ux=t[1]*nz-t[2]*ny,uy=t[2]*nx-t[0]*nz,uz=t[0]*ny-t[1]*nx;const ul=Math.hypot(ux,uy,uz);ux/=ul;uy/=ul;uz/=ul;
    const vx=ny*uz-nz*uy,vy=nz*ux-nx*uz,vz=nx*uy-ny*ux;this.u=[ux,uy,uz];this.v=[vx,vy,vz];
    const put=(i,a,r,wobble)=>{const c=Math.cos(a)*r*wobble,s=Math.sin(a)*r*wobble;this.base[i*3]=center[0]+ux*c+vx*s;this.base[i*3+1]=center[1]+uy*c+vy*s;this.base[i*3+2]=center[2]+uz*c+vz*s;};
    put(0,0,0,1);
    for(let ring=1;ring<=rings;ring++)for(let s=0;s<segments;s++){
      const i=1+(ring-1)*segments+s,a=(s+(ring%2)*.5)/segments*Math.PI*2;
      put(i,a,radius*ring/rings,1+(ring===rings?0:(random()-.5)*.04));
      if(ring===rings)this.fixed[i]=1;
    }
    this.x.set(this.base);this.prev.set(this.base);
    // Triangles and springs from the polar net.
    const tris=[],edges=new Map(),edge=(a,b)=>{const key=a<b?a+','+b:b+','+a;if(!edges.has(key))edges.set(key,[Math.min(a,b),Math.max(a,b)]);};
    for(let s=0;s<segments;s++){const a=1+s,b=1+(s+1)%segments;tris.push(0,a,b);edge(0,a);edge(a,b);}
    for(let ring=1;ring<rings;ring++)for(let s=0;s<segments;s++){
      const a=1+(ring-1)*segments+s,b=1+(ring-1)*segments+(s+1)%segments,c=a+segments,d=b+segments;
      tris.push(a,c,b,b,c,d);edge(a,c);edge(b,c);edge(b,d);edge(c,d);edge(a,b);
    }
    this.triangles=Uint32Array.from(tris);this.edges=[...edges.values()];
    this.restLength=Float64Array.from(this.edges,([a,b])=>Math.hypot(this.base[a*3]-this.base[b*3],this.base[a*3+1]-this.base[b*3+1],this.base[a*3+2]-this.base[b*3+2]));
    this.area0=this.area();
    // Gas inside the mochi that feeds the puff; large enough for a dome taller than a hemisphere when hot.
    this.reservoir=radius**3*12;this.gas=1;this.tearAt=2.9;this.quiet=0;this.lastOffset=[0,0,0];this.lastTemperature=1;this.lastSqueeze=0;this.temperature=1;this.squeeze=0;this.popped=false;this.vent=0;this.activity=0;this.onPop=null;
    this.normals=new Float64Array(n*3);
  }
  area(){
    const x=this.x,t=this.triangles;let total=0;
    for(let k=0;k<t.length;k+=3){
      const a=t[k]*3,b=t[k+1]*3,c=t[k+2]*3,e1=[x[b]-x[a],x[b+1]-x[a+1],x[b+2]-x[a+2]],e2=[x[c]-x[a],x[c+1]-x[a+1],x[c+2]-x[a+2]];
      total+=Math.hypot(e1[1]*e2[2]-e1[2]*e2[1],e1[2]*e2[0]-e1[0]*e2[2],e1[0]*e2[1]-e1[1]*e2[0])/2;
    }
    return total;
  }
  // Volume between the skin and the plane of its rim (tetrahedra from the centre).
  volume(){
    const x=this.x,t=this.triangles,c=this.center;let v=0;
    for(let k=0;k<t.length;k+=3){
      const a=t[k]*3,b=t[k+1]*3,d=t[k+2]*3;
      const ax=x[a]-c[0],ay=x[a+1]-c[1],az=x[a+2]-c[2],bx=x[b]-c[0],by=x[b+1]-c[1],bz=x[b+2]-c[2],dx=x[d]-c[0],dy=x[d+1]-c[1],dz=x[d+2]-c[2];
      v+=(ax*(by*dz-bz*dy)+ay*(bz*dx-bx*dz)+az*(bx*dy-by*dx))/6;
    }
    return Math.abs(v);
  }
  get stretch(){return this.area()/this.area0;}
  get pressure(){return this.gas*this.temperature*(1+this.squeeze)*this.reservoir/(this.reservoir+this.volume())-1;}
  // Rim positions follow the mochi; offset is how far the mochi surface moved.
  follow(offset){
    // A moving mochi wakes a resting puff.
    if(Math.abs(offset[0]-this.lastOffset[0])+Math.abs(offset[1]-this.lastOffset[1])+Math.abs(offset[2]-this.lastOffset[2])>1e-6*this.radius){this.quiet=0;this.lastOffset=[...offset];}
    for(let i=0;i<this.count;i++)if(this.fixed[i])for(let a=0;a<3;a++)this.x[i*3+a]=this.prev[i*3+a]=this.base[i*3+a]+offset[a];
  }
  pop(){
    if(this.popped)return false;
    // Mochi does not spring back: the stretched skin keeps its size and folds.
    const x=this.x;
    this.edges.forEach(([a,b],e)=>{this.restLength[e]=Math.hypot(x[a*3]-x[b*3],x[a*3+1]-x[b*3+1],x[a*3+2]-x[b*3+2])*.97;});
    this.popped=true;this.vent=1;this.activity=1;this.quiet=0;this.onPop?.();return true;
  }
  step(dt){
    if(!(dt>0))return;
    if(Math.abs(this.temperature-this.lastTemperature)>1e-4||Math.abs(this.squeeze-this.lastSqueeze)>1e-4){this.quiet=0;this.lastTemperature=this.temperature;this.lastSqueeze=this.squeeze;}
    // Asleep: a puff that has been still for a while costs nothing until woken.
    if(this.quiet>=.6&&this.vent<=0){this.activity=0;return;}
    const x=this.x,prev=this.prev,t=this.triangles,nrm=this.normals,n=this.count,substeps=4,h=dt/substeps;
    // A hot skin is softer; a popped one keeps its stretch (mochi does not spring back).
    const softness=Math.min(1,Math.max(0,(this.temperature-1)/.6)),compliance=.0002+.004*softness;
    if(this.vent>0){this.gas*=Math.exp(-dt*14);this.vent=Math.max(0,this.vent-dt*2);}
    let motion=0;
    for(let s=0;s<substeps;s++){
      const p=Math.max(this.popped?-.02:-.1,this.pressure);
      // Area-weighted vertex normals; pressure pushes along them.
      nrm.fill(0);
      for(let k=0;k<t.length;k+=3){
        const a=t[k]*3,b=t[k+1]*3,c=t[k+2]*3,e1x=x[b]-x[a],e1y=x[b+1]-x[a+1],e1z=x[b+2]-x[a+2],e2x=x[c]-x[a],e2y=x[c+1]-x[a+1],e2z=x[c+2]-x[a+2];
        let fx=e1y*e2z-e1z*e2y,fy=e1z*e2x-e1x*e2z,fz=e1x*e2y-e1y*e2x;
        if(fx*this.normal[0]+fy*this.normal[1]+fz*this.normal[2]<0){fx=-fx;fy=-fy;fz=-fz;}
        for(const i of [a,b,c]){nrm[i]+=fx/6;nrm[i+1]+=fy/6;nrm[i+2]+=fz/6;}
      }
      // Mass is proportional to skin area like the pressure force, so the
      // acceleration follows the unit normal. The skin's own weight makes it droop.
      const push=p*this.radius*28*h*h,weight=(this.popped?5.5:1.2)*this.radius*h*h,damping=Math.exp(-(this.popped&&this.vent<=0?14:6)*h);
      for(let i=0;i<n;i++){
        if(this.fixed[i])continue;const k=i*3,l=Math.hypot(nrm[k],nrm[k+1],nrm[k+2])||1;
        for(let a=0;a<3;a++){const v=(x[k+a]-prev[k+a])*damping;prev[k+a]=x[k+a];x[k+a]+=v+nrm[k+a]/l*push-(this.normal[a]-this.lean[a])*weight;}
      }
      // Springs, with compliance (XPBD-style softness) so the skin can stretch.
      const alpha=compliance/(h*h);
      for(let iteration=0;iteration<6;iteration++)for(let e=0;e<this.edges.length;e++){
        const [i,j]=this.edges[e],a=i*3,b=j*3,wi=this.fixed[i]?0:1,wj=this.fixed[j]?0:1;if(!wi&&!wj)continue;
        const dx=x[b]-x[a],dy=x[b+1]-x[a+1],dz=x[b+2]-x[a+2],d=Math.hypot(dx,dy,dz);if(d<1e-12)continue;
        // A popped skin hardly resists squashing, so the slack sags onto the
        // mochi and buckles into low wrinkles instead of standing as a cap.
        const rest=this.restLength[e],C=d-rest,give=C<0&&this.popped?.2:1;
        const lambda=-C*give/(wi+wj+alpha*(wi+wj)),gx=dx/d,gy=dy/d,gz=dz/d;
        x[a]-=gx*lambda*wi;x[a+1]-=gy*lambda*wi;x[a+2]-=gz*lambda*wi;x[b]+=gx*lambda*wj;x[b+1]+=gy*lambda*wj;x[b+2]+=gz*lambda*wj;
      }
      // The skin never sinks below the mochi surface.
      for(let i=0;i<n;i++){
        if(this.fixed[i])continue;const k=i*3,c=this.center,d=(x[k]-c[0])*this.normal[0]+(x[k+1]-c[1])*this.normal[1]+(x[k+2]-c[2])*this.normal[2];
        if(d<0){for(let a=0;a<3;a++){x[k+a]-=this.normal[a]*d;prev[k+a]=x[k+a];}}
      }
    }
    // Hot mochi skin flows: stretched springs slowly lengthen, so the puff keeps
    // growing until the gas pressure balances, or the skin tears.
    if(!this.popped&&softness>0)for(let e=0;e<this.edges.length;e++){
      const [i,j]=this.edges[e],d=Math.hypot(x[i*3]-x[j*3],x[i*3+1]-x[j*3+1],x[i*3+2]-x[j*3+2]),C=d-this.restLength[e];
      if(C>0)this.restLength[e]+=C*softness*1.4*dt;
    }
    if(!this.popped&&this.stretch>this.tearAt)this.pop();
    for(let k=0;k<n*3;k++)motion=Math.max(motion,Math.abs(x[k]-prev[k]));
    this.quiet=motion<4e-5*this.radius&&this.vent<=0?this.quiet+dt:0;
    this.activity=this.quiet<.6?1:0;
  }
  // How much taller than flat the skin stands, as a share of the rim radius.
  get height(){let top=0;const c=this.center;for(let i=0;i<this.count;i++){const k=i*3;top=Math.max(top,(this.x[k]-c[0])*this.normal[0]+(this.x[k+1]-c[1])*this.normal[1]+(this.x[k+2]-c[2])*this.normal[2]);}return top/this.radius;}
}
